if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');

        this.selectors = {
          variantIdSelect: '[name="id"]',
        };

        this._suppressMainVariantSyncUntil = 0;
      }

      syncStickySelectSelectedState(selectedVariantId) {
        const select = this.select;
        if (!select) return;

        const normalizedSelectedVariantId = String(selectedVariantId ?? '');
        Array.from(select.options).forEach((option) => {
          const isSelected = option.value === normalizedSelectedVariantId;
          option.selected = isSelected;
          if (isSelected) {
            option.setAttribute('selected', 'selected');
          } else {
            option.removeAttribute('selected');
          }
        });
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }
      get quantity() {
        return this.querySelector('quantity-input');
      }
      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
      get productId() {
        return this.getAttribute('data-product-id');
      }
      get select() {
        return this.querySelector('select');
      }
      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector(`.main-product-form[data-product-id="${this.productId}"]`);
        this.mainProductInfo = document.querySelector(`product-info[data-product-id="${this.productId}"]`);
        this.container = this.closest('.sticky-atc-bar');
        this.submitButton = this.querySelector('[type="submit"]');
        this.selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;

        this.variantData = this.getVariantData();

        this.init();
        this.select.addEventListener('change', () => {
          if (this.isUpdating) return;
          this.isUpdating = true;

          try {
            this._suppressMainVariantSyncUntil = (typeof performance !== 'undefined' ? performance.now() : Date.now()) + 500;

            this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
            this.selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;
            this.currentVariant = this.variantData.find((variant) => variant.id === Number(this.selectedVariantId));
            this.syncStickySelectSelectedState(this.selectedVariantId);

            if (!this.currentVariant) {
              this.updateButton(true, FoxTheme.variantStrings.unavailable, true);
              return;
            }

            if (this.mainVariantSelects) {
              this.syncMainVariantSelectors();

              this._suppressMainVariantSyncUntil = (typeof performance !== 'undefined' ? performance.now() : Date.now()) + 500;
            }

            this.updateQuantityInput();
            this.updatePrice();
            this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
          } finally {
            this.isUpdating = false;
          }
        });

        const hasRequiredFields = this.validateMainProductRequiredFields();
        if (hasRequiredFields) {
          this.submitButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.productFormActions.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => {
              this.productFormActions.requestSubmit();
            }, 300);
          });
        }
      }

      validateMainProductRequiredFields() {
        const mainForm = this.mainProductInfo;
        if (!mainForm) return true;
        const requiredFields = mainForm.querySelectorAll('[data-custom-property]');
        return requiredFields.length > 0;
      }

      syncMainVariantSelectors() {
        Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
          const variantOptionVal = this.currentVariant?.options?.[index];
          if (!variantOptionVal) return;

          switch (element.tagName) {
            case 'SELECT': {
              const selectedOption = element.querySelector(`option[value="${CSS.escape(variantOptionVal)}"]`);
              if (!selectedOption) return;

              Array.from(element.options).forEach((option) => {
                const isSelected = option === selectedOption;
                option.selected = isSelected;
                if (isSelected) {
                  option.setAttribute('selected', 'selected');
                } else {
                  option.removeAttribute('selected');
                }
              });

              if (element.value !== variantOptionVal) {
                element.value = variantOptionVal;
              }

              element.dispatchEvent(new Event('input', { bubbles: true }));
              element.dispatchEvent(new Event('change', { bubbles: true }));
              break;
            }
            case 'FIELDSET': {
              const targetRadio = element.querySelector(`input[type="radio"][value="${CSS.escape(variantOptionVal)}"]`);
              if (!targetRadio) return;

              if (!targetRadio.checked) {
                targetRadio.click();
              } else {
                targetRadio.dispatchEvent(new Event('input', { bubbles: true }));
                targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
              }

              this.syncFieldsetCheckedState(element, targetRadio);

              if (this.mainProductInfo && typeof this.mainProductInfo.updateSelectedSwatchValue === 'function') {
                this.mainProductInfo.updateSelectedSwatchValue({ target: targetRadio });
              }
              break;
            }
          }
        });

        if (this.mainProductInfo && typeof this.mainProductInfo.updateAllSwatchValueDisplays === 'function') {
          this.mainProductInfo.updateAllSwatchValueDisplays();
        }
      }

      syncFieldsetCheckedState(fieldset, targetRadio) {
        Array.from(fieldset.querySelectorAll('input[type="radio"]')).forEach((radio) => {
          const isSelected = radio === targetRadio;
          radio.checked = isSelected;
          radio.defaultChecked = isSelected;

          if (isSelected) {
            radio.setAttribute('checked', 'checked');
          } else {
            radio.removeAttribute('checked');
          }
        });
      }

      updateQuantityInput() {
        const selectedVariantId = Number(this.querySelector(this.selectors.variantIdSelect)?.value || this.currentVariant?.id);
        this.currentVariant = this.variantData.find((variant) => variant.id === selectedVariantId);
        if (!this.currentVariant) return;

        fetch(`${this.getAttribute('data-url')}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const quantity = document.querySelector(`#QuantitySticky-${this.sectionId}`);
            const newQuantity = parsedHTML.querySelector(`#QuantitySticky-${this.sectionId}`);
            if (newQuantity) {
              quantity.innerHTML = newQuantity.innerHTML;
            }
          })
          .catch((error) => console.error(error))
          .finally(() => {});
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }

        // Hide ATC bar by default on page load
        this.container.classList.remove('sticky-atc-bar--show');

        // Cache DOM elements for better performance
        this.buttonsContainer = document.querySelector('.product__info-wrapper .product-form__buttons');
        this.stickyHeader = document.querySelector('header.header[is="sticky-header"]');

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        // Use scroll event instead of IntersectionObserver for more precise control
        this.addScrollListener();
        
        // Initial evaluation on page load
        this.evaluateStickyAtcVisibility();
        
        this.syncWithMainProductForm();
      }

      addScrollListener() {
        this.onScroll = () => this.evaluateStickyAtcVisibility();
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      // The bar is re-rendered on an in-place product swap (TW-218); drop the old one's hooks.
      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        this.variantChangeUnsubscriber?.();
      }

      evaluateStickyAtcVisibility() {
        if (!this.buttonsContainer) return;
        
        const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
        
        // Calculate buttons position
        const rect = this.buttonsContainer.getBoundingClientRect();
        const rectCalculated = rect.top;
        
        const buttonsPosition = window.pageYOffset + rectCalculated;
        
        // Get sticky header height
        const headerHeight = this.stickyHeader ? this.stickyHeader.offsetHeight : 0;
        const totalHeight = this.buttonsContainer.offsetHeight + headerHeight;
        
        // Adjust viewport height by subtracting header height
        const adjustedViewportHeight = window.innerHeight - totalHeight;
        
        // Check if buttons are visible in the adjusted viewport
        const areButtonsVisible = rect.bottom > 0 && rectCalculated < adjustedViewportHeight;

        // Show ATC only if: scroll > buttons position AND buttons are not visible
        if (scrollPosition > buttonsPosition && !areButtonsVisible) {
          this.container.classList.add('sticky-atc-bar--show');
        } else {
          this.container.classList.remove('sticky-atc-bar--show');
        }
      }

      checkDevice(e) {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('#product-form-sticky-atc-bar');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updatePrice() {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat))
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${FoxTheme.Currency.getBaseUnit(this.currentVariant)}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');

          const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
          if (now < this._suppressMainVariantSyncUntil) {
            return;
          }

          this.currentVariant = variant;
          this.selectedVariantId = variant?.id ? String(variant.id) : '';
          variantInput.value = this.selectedVariantId;
          this.syncStickySelectSelectedState(this.selectedVariantId);
          this.updatePrice();
          this.updateButton(true, '', false);
          if (!variant) {
            // No variant found = unavailable (combination doesn't exist)
            this.updateButton(true, FoxTheme.variantStrings.unavailable, true);
          } else if (!variant.available) {
            // Variant exists but not available = sold out
            this.updateButton(true, FoxTheme.variantStrings.soldOut);
          } else {
            // Variant available = enable button
            this.updateButton(false, FoxTheme.variantStrings.addToCart);
          }

          this.updateQuantityInput();
        });
      }
    }
  );
}
